export const ARTIFACT_KINDS = {
  SOFTWARE: "software-requirements",
  SYSTEM: "system-requirements",
  SUBSYSTEM: "subsystem-requirements",
  DESIGN: "design-elements",
};

export const REVIEW_STATUS_LABELS_ORDER = [
  "Pending Review",
  "Approved",
  "Modified & Approved",
  "Rejected",
  "Needs Regeneration",
  "Needs More Context",
];

export const ARTIFACT_DEFINITIONS = {
  [ARTIFACT_KINDS.SOFTWARE]: {
    key: ARTIFACT_KINDS.SOFTWARE,
    artifactType: "code_architecture_software_requirement",
    idPrefix: "SWR",
    internalPrefix: "swr",
    title: "Software Requirements",
    description: "Requirements derived from the code-based functional decomposition.",
    sourceLabel: "Functional decomposition rows",
    deriveButton: "Derive from Functional Decomposition",
    addButton: "Add Requirement",
    sourceFeature: "Code-Based Architecture Software Requirements",
    sourceMethod: "Functional Decomposition Derivation",
    emptyMessage: "No software requirements have been derived yet.",
    noMatchMessage: "No software requirements match the active column filters.",
    columns: [
      { key: "id", label: "SW Requirement ID", width: 180 },
      {
        key: "source",
        label: "Requirement Source",
        width: 220,
        readOnly: true,
        getValue: (row = {}) => {
          const source = String(row.source || "").trim();
          if (source === "functional-derived") return "Functional Decomposition";
          if (source === "functional-derived-fallback") return "Functional Decomposition Fallback";
          if (source === "ai-generated") return "Functional Decomposition";
          if (source === "code-derived-fallback") return "Functional Decomposition Fallback";
          if (source === "hazard-derived") return "Hazard Analysis";
          if (source === "manual") return "Manual";
          return source || "Manual";
        },
      },
      { key: "sourceTraceId", label: "Source Functional Row", width: 210, linkType: "functional-row" },
      { key: "architectureSource", label: "Architecture Source", width: 240, readOnly: true, linkType: "architecture-source" },
      { key: "requirementText", label: "Requirement Text", width: 520, longText: true },
      { key: "derivedFromFunction", label: "Derived From Function", width: 260 },
      { key: "derivedFromInterface", label: "Derived From Interface", width: 260 },
      { key: "requirementType", label: "Requirement Type", width: 180 },
      { key: "priority", label: "Priority", width: 140 },
      { key: "rationale", label: "Rationale", width: 360, longText: true },
      { key: "linkedSourceCode", label: "Linked Source Code", width: 300 },
      { key: "linkedHazards", label: "Linked Hazards", width: 220, linkType: "hazard-row" },
      { key: "safetySignificant", label: "Safety Significant", width: 180, readOnly: true },
      { key: "safetySignificanceRationale", label: "Safety Significance Rationale", width: 360, longText: true, readOnly: true },
      { key: "updatedAt", label: "Last Updated", width: 220, readOnly: true },
    ],
    defaultRow: {
      requirementText: "",
      derivedFromFunction: "",
      derivedFromInterface: "",
      requirementType: "Functional",
      priority: "Medium",
      rationale: "",
      linkedSourceCode: "",
      linkedHazards: "",
      safetySignificant: "",
      safetySignificanceRationale: "",
      parentHazard: "",
      mitigationStrategy: "",
      criticalitySeverity: "",
      linkedVerification: "",
      linkedTests: "",
      sourceTraceId: "",
      source: "manual",
    },
  },
  [ARTIFACT_KINDS.SYSTEM]: {
    key: ARTIFACT_KINDS.SYSTEM,
    artifactType: "code_architecture_system_requirement",
    idPrefix: "SYS",
    internalPrefix: "sys",
    title: "System Requirements",
    description: "System-level requirements consolidated from software requirements.",
    sourceLabel: "Software source rows",
    deriveButton: "Derive from Software Requirements",
    addButton: "Add Requirement",
    sourceFeature: "Code-Based Architecture System Requirements",
    sourceMethod: "Software Requirements Derivation",
    sourceKind: ARTIFACT_KINDS.SOFTWARE,
    emptyMessage: "No system requirements have been derived yet.",
    noMatchMessage: "No system requirements match the active column filters.",
    columns: [
      { key: "id", label: "System Requirement ID", width: 190 },
      { key: "requirementText", label: "Requirement Text", width: 540, longText: true },
      { key: "derivedFrom", label: "Derived From", width: 220 },
      { key: "parentSwRequirement", label: "Parent SW Requirement", width: 240, linkType: "software-requirement" },
      { key: "architectureSource", label: "Architecture Source", width: 240, readOnly: true, linkType: "architecture-source" },
      { key: "rationale", label: "Rationale", width: 360, longText: true },
      { key: "verificationMethod", label: "Verification Method", width: 220 },
      { key: "linkedSubsystemRequirement", label: "Linked Subsystem Requirement", width: 300 },
      { key: "linkedDesignElement", label: "Linked Design Element", width: 260 },
      { key: "updatedAt", label: "Last Updated", width: 220, readOnly: true },
    ],
    defaultRow: {
      requirementText: "",
      derivedFrom: "Manual",
      parentSwRequirement: "",
      rationale: "",
      verificationMethod: "Analysis",
      linkedSubsystemRequirement: "",
      linkedDesignElement: "",
    },
  },
  [ARTIFACT_KINDS.SUBSYSTEM]: {
    key: ARTIFACT_KINDS.SUBSYSTEM,
    artifactType: "code_architecture_subsystem_requirement",
    idPrefix: "SUB",
    internalPrefix: "sub",
    title: "Subsystem Requirements",
    description: "Subsystem-level requirements consolidated and allocated from system requirements.",
    sourceLabel: "System source rows",
    deriveButton: "Derive from System Requirements",
    addButton: "Add Requirement",
    sourceFeature: "Code-Based Architecture Subsystem Requirements",
    sourceMethod: "System Requirements Derivation",
    sourceKind: ARTIFACT_KINDS.SYSTEM,
    emptyMessage: "No subsystem requirements have been derived yet.",
    noMatchMessage: "No subsystem requirements match the active column filters.",
    columns: [
      { key: "id", label: "Subsystem Requirement ID", width: 220 },
      { key: "subsystem", label: "Subsystem", width: 220 },
      { key: "requirementText", label: "Requirement Text", width: 540, longText: true },
      { key: "parentSystemRequirement", label: "Parent System Requirement", width: 270, linkType: "system-requirement" },
      { key: "architectureSource", label: "Architecture Source", width: 240, readOnly: true, linkType: "architecture-source" },
      { key: "allocatedFunction", label: "Allocated Function", width: 260, longText: true },
      { key: "allocatedArchitecture", label: "Allocated CSCI / CSC / CSU", width: 280 },
      { key: "rationale", label: "Rationale", width: 360, longText: true },
      { key: "verificationMethod", label: "Verification Method", width: 220 },
      { key: "linkedDesignElement", label: "Linked Design Element", width: 260 },
      { key: "updatedAt", label: "Last Updated", width: 220, readOnly: true },
    ],
    defaultRow: {
      subsystem: "Application Subsystem",
      requirementText: "",
      parentSystemRequirement: "",
      allocatedFunction: "",
      allocatedArchitecture: "",
      rationale: "",
      verificationMethod: "Analysis",
      linkedDesignElement: "",
    },
  },
  [ARTIFACT_KINDS.DESIGN]: {
    key: ARTIFACT_KINDS.DESIGN,
    artifactType: "code_architecture_design_element",
    idPrefix: "DES",
    internalPrefix: "des",
    title: "System / Subsystem Design",
    description: "Reviewable design elements derived from subsystem requirements.",
    sourceLabel: "Subsystem source rows",
    deriveButton: "Derive from Subsystem Requirements",
    addButton: "Add Design Element",
    sourceFeature: "Code-Based Architecture System / Subsystem Design",
    sourceMethod: "Subsystem Requirements Derivation",
    sourceKind: ARTIFACT_KINDS.SUBSYSTEM,
    emptyMessage: "No design elements have been derived yet.",
    noMatchMessage: "No design elements match the active column filters.",
    columns: [
      { key: "id", label: "Design Element ID", width: 190 },
      { key: "designElementName", label: "Design Element Name", width: 280 },
      { key: "designLevel", label: "Design Level", width: 180 },
      { key: "description", label: "Description", width: 460, longText: true },
      { key: "parentRequirement", label: "Parent Requirement", width: 240, linkType: "subsystem-requirement" },
      { key: "architectureSource", label: "Architecture Source", width: 240, readOnly: true, linkType: "architecture-source" },
      { key: "allocatedFunction", label: "Allocated Function", width: 260, longText: true },
      { key: "allocatedArchitecture", label: "Allocated CSCI / CSC / CSU", width: 280 },
      { key: "interfaceDependencies", label: "Interface Dependencies", width: 320, longText: true },
      { key: "designRationale", label: "Design Rationale", width: 360, longText: true },
      { key: "linkedSourceCode", label: "Linked Source Code", width: 300 },
      { key: "updatedAt", label: "Last Updated", width: 220, readOnly: true },
    ],
    defaultRow: {
      designElementName: "",
      designLevel: "Subsystem",
      description: "",
      parentRequirement: "",
      allocatedFunction: "",
      allocatedArchitecture: "",
      interfaceDependencies: "",
      designRationale: "",
      linkedSourceCode: "",
    },
  },
};

export const TRACEABILITY_MATRIX_COLUMNS = [
  { key: "traceChainId", label: "Trace Chain ID", width: 180 },
  { key: "functionalRow", label: "Functional Row", width: 220, linkType: "functional-row" },
  { key: "architectureSource", label: "Architecture Source", width: 240, linkType: "architecture-source" },
  { key: "sourceFunction", label: "Source Function", width: 260 },
  { key: "controlAction", label: "Control Action / Interface", width: 260 },
  { key: "targetFunction", label: "Target Function", width: 260 },
  { key: "softwareRequirement", label: "Software Requirement", width: 240, linkType: "software-requirement" },
  { key: "systemRequirement", label: "System Requirement", width: 240, linkType: "system-requirement" },
  { key: "subsystemRequirement", label: "Subsystem Requirement", width: 260, linkType: "subsystem-requirement" },
  { key: "designElement", label: "Design Element", width: 240, linkType: "design-element" },
  { key: "sourceFiles", label: "Source Files", width: 300 },
  { key: "allocatedArchitecture", label: "CSCI / CSC / CSU", width: 280 },
  { key: "traceCompleteness", label: "Trace Completeness", width: 220 },
  { key: "openIssues", label: "Open Issues", width: 360, longText: true },
];
